import { useMe } from "@hooks/api";
import { useAuth, useClerk, useUser } from "@clerk/clerk-react";
import { Spinner } from "@medusajs/icons";
import { Alert, Button, Text } from "@medusajs/ui";
import { SearchProvider } from "@providers/search-provider";
import { SidebarProvider } from "@providers/sidebar-provider";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { sdk } from "../../../lib/client";
import { queryClient } from "../../../lib/query-client";

export const ProtectedRoute = () => {
  const { isSignedIn, isLoaded: clerkLoaded, getToken } = useAuth();
  const { user: clerkUser, isLoaded: clerkUserLoaded } = useUser();
  const clerk = useClerk();
  const { user, isLoading: meLoading } = useMe();
  const location = useLocation();
  const [bridging, setBridging] = useState(false);
  const [bridgeError, setBridgeError] = useState<string | null>(null);
  const bridgeAttempted = useRef(false);

  const isAdmin = (clerkUser?.publicMetadata as any)?.isAdmin === true;
  const adminRoles = (clerkUser?.publicMetadata as any)?.adminRole as string[] | undefined;
  const isOwner = Array.isArray(adminRoles) && adminRoles.includes("owner");
  const isAuthorized = isAdmin && isOwner;

  useEffect(() => {
    if (!clerkLoaded || !clerkUserLoaded || !isSignedIn || bridgeAttempted.current) {
      return;
    }

    if (!isAuthorized) {
      return;
    }

    if (user || meLoading) {
      return;
    }

    bridgeAttempted.current = true;
    setBridging(true);

    getToken().then(async (clerkToken) => {
      if (!clerkToken) {
        setBridgeError("Could not get Clerk session token");
        setBridging(false);
        return;
      }

      try {
        await sdk.auth.login("user", "clerk", { token: clerkToken });
        await queryClient.invalidateQueries({ queryKey: ["users", "me"] });
      } catch (err: any) {
        console.error("Failed to bridge Clerk token to Medusa:", err);
        setBridgeError(err?.message || "Failed to authenticate with Medusa");
      } finally {
        setBridging(false);
      }
    });
  }, [clerkLoaded, clerkUserLoaded, isSignedIn, isAuthorized, user, meLoading, getToken]);

  if (!clerkLoaded || !clerkUserLoaded || meLoading || bridging) {
    return (
      <div
        className="flex min-h-screen items-center justify-center"
        data-testid="protected-route-loading"
      >
        <Spinner
          className="animate-spin text-ui-fg-interactive"
          data-testid="protected-route-spinner"
        />
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <Navigate
        to="/login"
        state={{ from: location }}
        replace
      />
    );
  }

  // Signed in via Clerk but not authorized as admin
  if (!isAuthorized) {
    const handleSignOut = async () => {
      await sdk.auth.logout().catch(() => {});
      await clerk.signOut();
      queryClient.clear();
      window.location.href = "/login";
    };

    return (
      <div className="flex min-h-screen items-center justify-center bg-ui-bg-subtle">
        <div className="flex max-w-[400px] flex-col items-center gap-y-4 p-4">
          <Text size="large" weight="plus">
            Access Denied
          </Text>
          <Alert variant="error" dismissible={false}>
            Your account does not have admin access. Contact your administrator to request access.
          </Alert>
          <Button variant="secondary" onClick={handleSignOut}>
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  // Clerk is authorized but Medusa session failed
  if (!user) {
    const handleSignOut = async () => {
      await sdk.auth.logout().catch(() => {});
      await clerk.signOut();
      queryClient.clear();
      window.location.href = "/login";
    };

    return (
      <div className="flex min-h-screen items-center justify-center bg-ui-bg-subtle">
        <div className="flex max-w-[400px] flex-col items-center gap-y-4 p-4">
          <Text size="large" weight="plus">
            Authentication Error
          </Text>
          <Alert variant="error" dismissible={false}>
            {bridgeError || "Could not link your account. Make sure your Clerk email matches a Medusa admin user."}
          </Alert>
          <Button variant="secondary" onClick={handleSignOut}>
            Sign out and try again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <SearchProvider>
        <Outlet />
      </SearchProvider>
    </SidebarProvider>
  );
};
